import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';

export interface BiometricCredential {
  credentialId: string;
  publicKey: string;
  userHandle: string;
}

export interface BiometricAuthOptions {
  title?: string;
  subtitle?: string;
  description?: string;
  fallbackLabel?: string;
  negativeButtonText?: string;
}

@Injectable({
  providedIn: 'root'
})
export class BiometricAuthService {
  private isSupportedSubject = new BehaviorSubject<boolean>(false);
  public isSupported$ = this.isSupportedSubject.asObservable();

  private isAvailableSubject = new BehaviorSubject<boolean>(false);
  public isAvailable$ = this.isAvailableSubject.asObservable();

  constructor() {
    this.checkSupport();
  }

  private async checkSupport(): Promise<void> {
    try {
      // Verificar soporte WebAuthn
      const isWebAuthnSupported =
        'credentials' in navigator &&
        'create' in navigator.credentials &&
        'get' in navigator.credentials &&
        window.PublicKeyCredential &&
        typeof window.PublicKeyCredential === 'function';

      this.isSupportedSubject.next(isWebAuthnSupported);

      if (isWebAuthnSupported) {
        // Verificar si hay autenticadores disponibles
        const available = await PublicKeyCredential.isUserVerifyingPlatformAuthenticatorAvailable();
        this.isAvailableSubject.next(available);
      }
    } catch (error) {
      console.error('Error verificando soporte biométrico:', error);
      this.isSupportedSubject.next(false);
      this.isAvailableSubject.next(false);
    }
  }

  // Verificar si la autenticación biométrica está soportada
  isSupported(): boolean {
    return this.isSupportedSubject.value;
  }

  // Verificar si hay autenticadores biométricos disponibles
  isAvailable(): boolean {
    return this.isAvailableSubject.value;
  }

  // Registrar credenciales biométricas para el usuario
  async registerBiometric(userId: string, userName: string, userEmail: string): Promise<BiometricCredential> {
    if (!this.isSupported() || !this.isAvailable()) {
      throw new Error('Autenticación biométrica no disponible');
    }

    try {
      // Generar challenge aleatorio
      const challenge = this.generateChallenge();
      const userHandle = this.stringToArrayBuffer(userId);

      const publicKeyCredentialCreationOptions: PublicKeyCredentialCreationOptions = {
        challenge,
        rp: {
          name: 'BarClik',
          id: window.location.hostname,
        },
        user: {
          id: userHandle,
          name: userEmail,
          displayName: userName,
        },
        pubKeyCredParams: [
          {
            alg: -7, // ES256
            type: 'public-key',
          },
          {
            alg: -257, // RS256
            type: 'public-key',
          },
        ],
        authenticatorSelection: {
          authenticatorAttachment: 'platform', // Solo autenticadores integrados (Touch ID, Face ID, Windows Hello)
          userVerification: 'required',
          requireResidentKey: false,
        },
        timeout: 60000,
        attestation: 'direct',
      };

      const credential = await navigator.credentials.create({
        publicKey: publicKeyCredentialCreationOptions,
      }) as PublicKeyCredential;

      if (!credential) {
        throw new Error('No se pudo crear la credencial biométrica');
      }

      // Procesar la credencial
      const response = credential.response as AuthenticatorAttestationResponse;
      const publicKey = response.getPublicKey();
      if (!publicKey) {
        throw new Error('No se pudo obtener la llave pública de la credencial');
      }

      const credentialData: BiometricCredential = {
        credentialId: this.arrayBufferToBase64(credential.rawId),
        publicKey: this.arrayBufferToBase64(publicKey),
        userHandle: this.arrayBufferToBase64(userHandle),
      };

      // Guardar credenciales localmente
      this.saveBiometricCredential(userId, credentialData);

      return credentialData;
    } catch (error) {
      console.error('Error registrando autenticación biométrica:', error);
      throw new Error('Error al configurar la autenticación biométrica');
    }
  }

  // Autenticar usando credenciales biométricas
  async authenticateBiometric(userId?: string, options?: BiometricAuthOptions): Promise<boolean> {
    if (!this.isSupported() || !this.isAvailable()) {
      throw new Error('Autenticación biométrica no disponible');
    }

    try {
      // Obtener credenciales guardadas
      const savedCredentials = this.getSavedCredentials(userId);
      if (savedCredentials.length === 0) {
        throw new Error('No hay credenciales biométricas registradas');
      }

      const challenge = this.generateChallenge();

      const publicKeyCredentialRequestOptions: PublicKeyCredentialRequestOptions = {
        challenge,
        allowCredentials: savedCredentials.map(cred => ({
          id: this.base64ToArrayBuffer(cred.credentialId),
          type: 'public-key' as const,
          transports: ['internal'] as AuthenticatorTransport[],
        })),
        timeout: 60000,
        userVerification: 'required',
      };

      const credential = await navigator.credentials.get({
        publicKey: publicKeyCredentialRequestOptions,
      }) as PublicKeyCredential;

      if (!credential) {
        return false;
      }

      // Verificar que la credencial coincide con las guardadas
      const credentialId = this.arrayBufferToBase64(credential.rawId);
      const isValidCredential = savedCredentials.some(cred => cred.credentialId === credentialId);

      return isValidCredential;
    } catch (error: any) {
      console.error('Error en autenticación biométrica:', error);
      if (error?.name === 'NotAllowedError') {
        throw new Error('Autenticación biométrica cancelada por el usuario');
      } else if (error?.name === 'NotSupportedError') {
        throw new Error('Autenticación biométrica no soportada');
      }
      return false;
    }
  }

  // Verificar si un usuario tiene credenciales biométricas registradas
  hasBiometricCredentials(userId?: string): boolean {
    return this.getSavedCredentials(userId).length > 0;
  }

  // Eliminar credenciales biométricas de un usuario
  removeBiometricCredentials(userId: string): void {
    const key = `biometric_credentials_${userId}`;
    localStorage.removeItem(key);
  }

  // Obtener tipo de autenticación biométrica disponible
  getBiometricType(): string {
    const userAgent = navigator.userAgent.toLowerCase();

    if (/iphone|ipad|ipod/.test(userAgent)) {
      return 'Touch ID / Face ID';
    } else if (/android/.test(userAgent)) {
      return 'Huella dactilar';
    } else if (/windows/.test(userAgent)) {
      return 'Windows Hello';
    } else if (/mac/.test(userAgent)) {
      return 'Touch ID';
    }

    return 'Autenticación biométrica';
  }

  // Métodos privados
  private generateChallenge(): ArrayBuffer {
    const buffer = new Uint8Array(32);
    crypto.getRandomValues(buffer);
    return buffer.buffer as ArrayBuffer;
  }

  private stringToArrayBuffer(str: string): ArrayBuffer {
    const encoder = new TextEncoder();
    return encoder.encode(str).buffer as ArrayBuffer;
  }

  private arrayBufferToBase64(buffer: ArrayBuffer): string {
    const bytes = new Uint8Array(buffer);
    let binary = '';
    bytes.forEach(byte => binary += String.fromCharCode(byte));
    return btoa(binary);
  }

  private base64ToArrayBuffer(base64: string): ArrayBuffer {
    const binary = atob(base64);
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i++) {
      bytes[i] = binary.charCodeAt(i);
    }
    return bytes.buffer;
  }

  private saveBiometricCredential(userId: string, credential: BiometricCredential): void {
    const key = `biometric_credentials_${userId}`;
    const existing = this.getSavedCredentials(userId);

    // Añadir nueva credencial (evitar duplicados)
    const updated = existing.filter(cred => cred.credentialId !== credential.credentialId);
    updated.push(credential);

    localStorage.setItem(key, JSON.stringify(updated));
  }

  private getSavedCredentials(userId?: string): BiometricCredential[] {
    if (!userId) {
      // Intentar obtener credenciales de cualquier usuario guardado
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key?.startsWith('biometric_credentials_')) {
          const credentialsStr = localStorage.getItem(key);
          if (credentialsStr) {
            try {
              return JSON.parse(credentialsStr);
            } catch (e) {
              continue;
            }
          }
        }
      }
      return [];
    }

    const key = `biometric_credentials_${userId}`;
    const credentialsStr = localStorage.getItem(key);

    if (!credentialsStr) {
      return [];
    }

    try {
      return JSON.parse(credentialsStr);
    } catch (error) {
      console.error('Error parsing biometric credentials:', error);
      return [];
    }
  }
}